/**
 * Worker API Client
 * Client goi Workers API
 * 
 * V3: Chuyen sang Jobs architecture
 * - insertJob(): INSERT vao bang jobs -> trigger -> Worker xu ly
 * - Xoa cac SSE functions cu (qualityCheck, audit, semanticMatch)
 * - Giu lai: pending sync, pending results, health check
 */

import { supabase } from '../supabase/supabaseClient';

// Workers URL
const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

/**
 * Tao requestId duy nhat
 */
export function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get JWT token tu session hien tai
 */
async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Chua dang nhap. Vui long dang nhap lai.');
  }
  return session.access_token;
}

/**
 * Goi Workers API (GET/DELETE)
 */
async function callWorkersMethod<T>(endpoint: string, method: 'GET' | 'DELETE'): Promise<T> {
  const token = await getAuthToken();

  const response = await fetch(`${WORKERS_URL}${endpoint}`, {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error((error as any).error || `Loi server: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// ===== INSERT Job (V3: thay the SSE calls) =====

export type JobType = 'translate' | 'quality-check' | 'audit' | 'semantic-match' | 'translate-full';

export interface InsertJobParams {
  type: JobType;
  input: Record<string, any>;
  fileId: string;
  fileName: string;
  batchIndex?: number;
  totalBatches?: number;
  runId?: string;
  estimatedTokens?: number;
  retryOf?: string;                                // US-13-04: parent job_id if retry attempt
  privacyMode?: 'cloud_sync' | 'incognito';        // US-12-01: per-job privacy mode
  // Billing mode (mirrors backend `createJob.ts`):
  //   'train_pre_translate_audit' → audit factor (train chain)
  //   'qc_bundle' → QC factor (translate bundle preflight)
  //   'audit_bundle' → audit factor (standalone audit using bundle credit)
  //   undefined / 'standalone_audit' → real prompt formula (default)
  billingContext?:
    | 'standalone_audit'
    | 'train_pre_translate_audit'
    | 'qc_bundle'
    | 'audit_bundle';
  // Train chain: FK to audit job this job feeds into (translate + match jobs only)
  updateTo?: string;
  // Train chain: audit job created as 'waiting' until M+T deps done; others use 'pending' (default)
  initialStatus?: 'pending' | 'waiting';
}

// Read current privacy mode from Zustand store (imported lazily to avoid circular dep)
function getCurrentPrivacyMode(): 'cloud_sync' | 'incognito' {
  try {
    // Lazy import to avoid circular; store module is independent
    const mod = require('../../store/usePrivacyStore');
    return mod.usePrivacyStore?.getState?.().mode || 'cloud_sync';
  } catch {
    return 'cloud_sync';
  }
}

/**
 * US-19-01 Task 1 — Upfront deduct billing
 * Build request body for POST /api/jobs from InsertJobParams.
 * Flattens input.config fields into top-level per Worker contract.
 */
function buildCreateJobBody(params: InsertJobParams, runId: string, totalBatches: number): any {
  const input = (params.input || {}) as any;
  const config = input.config || {};
  const privacyMode = params.privacyMode === 'incognito' ? 'incognito' : 'cloud_sync';

  const body: any = {
    type: params.type,
    // IP protection: KHÔNG gửi model — Worker hardcode server-side
    sourceLang: config.sourceLang,
    targetLang: config.targetLang,
    file_id: params.fileId,
    file_name: params.fileName,
    run_id: runId,
    batch_index: params.batchIndex ?? 0,
    batch_count: totalBatches,
    privacy_mode: privacyMode,
    retry_of: params.retryOf || null,
  };
  if (params.billingContext) {
    body.billing_context = params.billingContext;
  }
  // Train chain pass-through fields
  if (params.updateTo) {
    body.update_to = params.updateTo;
  }
  if (params.initialStatus) {
    body.initial_status = params.initialStatus;
  }

  // Type-specific payload shapes (mirror handleCountTokens / handleCreateJob contract)
  if (params.type === 'translate') {
    body.segments = input.segments || [];
  } else if (params.type === 'quality-check') {
    body.sourceSegments = input.sourceSegments;
    body.aiSegments = input.aiSegments;
    body.extraRules = config.extraRules;
  } else if (params.type === 'audit') {
    body.sourceSegments = input.sourceSegments;
    body.aiSegments = input.aiSegments;
    body.humanSegments = input.humanSegments;
    body.systemInstruction = config.systemInstruction;
  } else if (params.type === 'semantic-match') {
    body.sourceSegments = input.viSegments || input.sourceSegments;
    body.aiSegments = input.enSegments || input.aiSegments;
  }

  return body;
}

/**
 * POST /api/jobs — Worker gate that recomputes authoritative cost, charges user atomically,
 * creates the job row, and returns job_id. Replaces direct supabase.from('jobs').insert().
 *
 * Error handling:
 *   - 402 INSUFFICIENT_CREDITS → throws Error with .code='INSUFFICIENT_CREDITS', .estimated_credits
 *   - 400/403/500 → throws Error with .status + .code
 */
async function postCreateJob(params: InsertJobParams, runId: string, totalBatches: number): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Chua dang nhap');

  const body = buildCreateJobBody(params, runId, totalBatches);
  // US-19-21 B-4: idempotency key — UUID per job creation, server dedupes 1h.
  // Stable per (runId, batchIndex) so client retry returns cached response.
  const idempotencyKey = `${runId}:${params.batchIndex ?? 0}:${params.type}`;
  const res = await fetch(`${WORKERS_URL}/api/jobs`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let parsed: any = null;
  try { parsed = JSON.parse(text); } catch {}

  if (!res.ok) {
    const err = new Error(parsed?.error || `Create job failed ${res.status}`) as any;
    err.status = res.status;
    err.code = parsed?.code;
    err.estimated_credits = parsed?.estimated_credits;
    // US-19-21 C-5: 429 USER_RATE_LIMIT structured error
    if (res.status === 429) {
      err.limit_type = parsed?.limit_type;
      err.retry_after_seconds = parsed?.retry_after_seconds;
    }
    // US-19-21 E-2/E-3: 503 SERVICE_BUSY (global quota saturated)
    if (res.status === 503) {
      err.saturated = parsed?.saturated;
    }
    // US-19-21 B-2: 413 FILE_TOO_LARGE
    if (res.status === 413) {
      err.code = 'FILE_TOO_LARGE';
    }
    throw err;
  }

  if (!parsed?.job_id) {
    throw new Error('Create job response missing job_id');
  }
  return parsed.job_id;
}

/**
 * INSERT job via Worker /api/jobs gate (upfront deduct + create atomic)
 * Tra ve jobId de subscribe Realtime
 */
export async function insertJob(params: InsertJobParams): Promise<string> {
  const runId = params.runId || crypto.randomUUID();
  const privacyMode = params.privacyMode || getCurrentPrivacyMode();
  const paramsWithMode = { ...params, privacyMode };
  return postCreateJob(paramsWithMode, runId, params.totalBatches ?? 1);
}

/**
 * INSERT nhieu jobs cung luc (cho batch translate) via Worker gate
 * Mỗi job = 1 call /api/jobs (charge atomic riêng) — serialize bởi row-lock balance
 * Tra ve array jobIds
 */
export async function insertJobs(jobs: InsertJobParams[]): Promise<string[]> {
  if (jobs.length === 0) return [];

  const runId = jobs[0]?.runId || crypto.randomUUID();
  const sharedPrivacyMode = getCurrentPrivacyMode();
  const totalBatches = jobs.length;

  // Sequential — balance row-locked in DB, parallel calls would serialize anyway
  // Also fail-fast: if job #3 runs out of credit, stop charging #4+
  const jobIds: string[] = [];
  for (let i = 0; i < jobs.length; i++) {
    const j = jobs[i];
    const paramsWithMeta = {
      ...j,
      runId,
      batchIndex: j.batchIndex ?? i,
      totalBatches,
      privacyMode: j.privacyMode || sharedPrivacyMode,
    };
    try {
      const id = await postCreateJob(paramsWithMeta, runId, totalBatches);
      jobIds.push(id);
    } catch (err: any) {
      if (err.code === 'INSUFFICIENT_CREDITS' && jobIds.length > 0) {
        // Partial success — some batches charged + created, then ran out
        err.message = `Không đủ credit cho batch ${i + 1}/${totalBatches} (đã tạo ${jobIds.length} batches trước). ${err.message}`;
      }
      throw err;
    }
  }
  return jobIds;
}

// ===== Run Job & Wait for Result =====

export interface RunJobResult {
  result: any;
  usage: { inputTokens: number; outputTokens: number; cost: number };
  jobId: string;
  promptInput?: string;
  rawResponse?: string;
}

/**
 * Luong chung cho tat ca AI tasks:
 * 1. INSERT job vao bang jobs (trigger -> Worker -> agent/chain)
 * 2. Subscribe Realtime doi job done/error
 * 3. Doc pending_sync lay result
 * 4. Xoa pending_sync
 * 5. Tra result ve frontend
 *
 * Usage:
 *   const { result, usage } = await runJobAndWait({
 *     type: 'quality-check',
 *     fileId, fileName,
 *     input: { sourceSegments, aiSegments, config }
 *   });
 */
export async function runJobAndWait(
  params: InsertJobParams,
  timeoutMs = 600_000
): Promise<RunJobResult> {
  const jobId = await insertJob(params);

  return new Promise<RunJobResult>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      settled = true;
      clearTimeout(timeout);
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };

    const timeout = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error(`Job timeout sau ${Math.round(timeoutMs / 1000)}s`));
    }, timeoutMs);

    // Handler khi job done/error
    const handleJobUpdate = async (job: any) => {
      if (settled) return;
      if (job.status !== 'done' && job.status !== 'error') return;

      cleanup();

      if (job.status === 'error') {
        reject(new Error(job.error || 'Job failed'));
        return;
      }

      // Doc pending_sync
      let syncRow: any = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        // US-19-01 Task 3: prompt_input/raw_response moved to private job_debug_logs.
        // Admin Debug Lab fetches via /api/admin/debug-log/:jobId Worker endpoint.
        const { data } = await supabase.from('pending_sync')
          .select('result').eq('job_id', jobId).limit(1);
        if (data && data.length > 0) {
          syncRow = data[0];
          break;
        }
        // Retry -- pending_sync co the chua commit xong
        if (attempt < 2) await new Promise(r => setTimeout(r, 500));
      }

      // Song song: DELETE pending_sync + UPDATE jobs.synced_at = now() để audit.
      // RLS policy "User update own synced_at on jobs" + column-level GRANT chỉ cho phép UPDATE synced_at.
      await Promise.allSettled([
        supabase.from('pending_sync').delete().eq('job_id', jobId),
        supabase.from('jobs').update({ synced_at: new Date().toISOString() }).eq('id', jobId),
      ]);

      resolve({
        result: syncRow?.result || {},
        // US-19-01 Task 3: prompt/raw moved to private table; admin fetches via separate endpoint
        promptInput: undefined,
        rawResponse: undefined,
        usage: {
          inputTokens: job.input_tokens || 0,
          outputTokens: job.output_tokens || 0,
          cost: job.cost_usd || 0,
        },
        jobId,
      });
    };

    // Subscribe Realtime TRUOC
    const channel = supabase.channel(`job-wait-${jobId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'jobs',
        filter: `id=eq.${jobId}`,
      }, (payload) => {
        handleJobUpdate(payload.new);
      })
      .subscribe();

    // Polling fallback moi 5s (phong truong hop miss Realtime event)
    const pollInterval = setInterval(async () => {
      if (settled) return;
      const { data } = await supabase.from('jobs')
        .select('status, error, input_tokens, output_tokens, cost_usd')
        .eq('id', jobId).limit(1);
      if (data && data.length > 0) {
        handleJobUpdate(data[0]);
      }
    }, 5000);
  });
}

// ===== Translate Stream (US-12-01 incognito SSE direct) =====

/**
 * runTranslateStream — incognito-only direct SSE translation.
 *
 * Per US-12-01 contract A-5/B-1:
 *   1. POST /api/translate-stream with body {segments, config, file_id, file_name, run_id, batch_index, batch_count, privacy_mode:'incognito'}
 *   2. CHECK response.ok BEFORE reading body — pre-stream HTTP errors (402/4xx/5xx) return JSON, not SSE
 *   3. If ok → parse SSE event stream:
 *      - `event: ping` / `event: heartbeat` → keep-alive, ignore data
 *      - `event: result` → resolve with {result:{translated[]}, usage}
 *      - `event: error` → reject with Error + .code/.errorId/.refunded
 *      - `event: diagnostic` → log debug, continue
 *   4. Reader EOF without result/error → reject STREAM_INCOMPLETE
 *
 * Caller responsible for retry: each retry creates fresh stream call with new charge.
 * Async refund of failed prior attempt handled server-side (best-effort handler + watchdog).
 */
export async function runTranslateStream(params: InsertJobParams): Promise<RunJobResult> {
  const token = await getAuthToken();
  const runId = params.runId || crypto.randomUUID();
  const totalBatches = params.totalBatches ?? 1;
  const input = (params.input || {}) as any;
  const config = input.config || {};

  const body = {
    segments: input.segments || [],
    config: {
      // IP protection: KHÔNG gửi model — Worker hardcode server-side
      sourceLang: config.sourceLang,
      targetLang: config.targetLang,
      systemInstruction: config.systemInstruction,
      extraRules: config.extraRules,
      locale: config.locale,
      temperature: config.temperature,
      maxOutputTokens: config.maxOutputTokens,
    },
    file_id: params.fileId,
    file_name: params.fileName,
    run_id: runId,
    batch_index: params.batchIndex ?? 0,
    batch_count: totalBatches,
    privacy_mode: 'incognito' as const,
  };

  const response = await fetch(`${WORKERS_URL}/api/translate-stream`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  // Step 2: pre-stream HTTP error gate (B-1 step 2)
  if (!response.ok) {
    let errBody: any = null;
    try { errBody = await response.json(); } catch { /* non-JSON error body */ }
    const err = new Error(errBody?.error || `HTTP ${response.status} ${response.statusText}`) as any;
    err.status = response.status;
    err.code = errBody?.code;
    err.estimated_credits = errBody?.estimated_credits;
    throw err;
  }

  if (!response.body) {
    throw new Error('Stream response missing body');
  }

  // Step 3: parse SSE event stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let resultPayload: any = null;
  let errorPayload: any = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events separated by \n\n
      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        if (!rawEvent.trim()) continue;

        // Parse event lines
        let eventType = 'message';
        let dataLine = '';
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLine += line.slice(5).trim();
          }
        }

        let data: any = null;
        if (dataLine) {
          try { data = JSON.parse(dataLine); } catch { /* keep raw */ data = dataLine; }
        }

        switch (eventType) {
          case 'result':
            resultPayload = data;
            break;
          case 'error':
            errorPayload = data;
            break;
          case 'ping':
          case 'heartbeat':
          case 'diagnostic':
            // keep-alive / debug, ignore
            break;
          default:
            console.debug(`[runTranslateStream] unknown event "${eventType}"`);
        }

        if (resultPayload || errorPayload) break;
      }

      if (resultPayload || errorPayload) break;
    }
  } finally {
    try { reader.releaseLock(); } catch { /* ignore */ }
  }

  if (errorPayload) {
    const err = new Error(errorPayload.error || 'Stream error') as any;
    err.code = errorPayload.code;
    err.errorId = errorPayload.errorId;
    err.refunded = errorPayload.refunded;
    throw err;
  }

  if (!resultPayload) {
    const err = new Error('Stream closed without result event') as any;
    err.code = 'STREAM_INCOMPLETE';
    throw err;
  }

  // Step 4: shape into RunJobResult
  return {
    result: { translated: resultPayload.translated },
    usage: {
      inputTokens: resultPayload.inputTokens || 0,
      outputTokens: resultPayload.outputTokens || 0,
      cost: resultPayload.costUsd || 0,
    },
    jobId: '',  // SSE path has no client-visible job_id (server tracks internally)
  };
}

// ===== Wait for Batch Jobs (Multi-batch) =====

export interface BatchJobsResult {
  /** Ket qua tu pending_sync, sorted theo batch_index. US-19-01 Task 3: prompt/raw moved to private table. */
  syncRows: { batch_index: number; result: any; type?: string; job_id?: string }[];
  /** Aggregate usage tu tat ca jobs */
  usage: { inputTokens: number; outputTokens: number; cost: number };
  /** So batch done */
  doneCount: number;
  /** So batch error */
  errorCount: number;
}

export interface BatchProgressCallback {
  (progress: {
    completedBatches: number;
    totalBatches: number;
    percent: number;
    currentStatus: string;
  }): void;
}

/**
 * Doi tat ca batch jobs cung run_id hoan thanh.
 * Tuong tu runJobAndWait nhung cho multi-batch.
 *
 * 1. Subscribe Realtime doi jobs UPDATE
 * 2. Polling fallback moi 5s
 * 3. Khi tat ca done/error -> doc pending_sync
 * 4. Xoa pending_sync
 * 5. Tra ve ket qua
 */
export async function waitForBatchJobs(
  runId: string,
  totalBatches: number,
  onProgress?: BatchProgressCallback,
): Promise<BatchJobsResult> {
  return new Promise<BatchJobsResult>((resolve, reject) => {
    let settled = false;
    const batchStatuses = new Map<number, { status: string; inputTokens: number; outputTokens: number; costUsd: number; error?: string }>();

    const cleanup = () => {
      settled = true;
      clearTimeout(timeout);
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };

    const timeout = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error(`Batch jobs timeout sau 10 phut`));
    }, 600_000);

    // Check if all batches are done/error
    const checkComplete = async () => {
      if (settled) return;

      const doneOrError = [...batchStatuses.values()].filter(
        b => b.status === 'done' || b.status === 'error'
      ).length;

      // Report progress
      if (onProgress) {
        const doneCount = [...batchStatuses.values()].filter(b => b.status === 'done').length;
        onProgress({
          completedBatches: doneCount,
          totalBatches,
          percent: Math.round((doneCount / Math.max(1, totalBatches)) * 100),
          currentStatus: doneOrError >= totalBatches ? 'complete' : 'running',
        });
      }

      if (doneOrError < totalBatches) return;

      // All batches settled
      cleanup();

      const doneCount = [...batchStatuses.values()].filter(b => b.status === 'done').length;
      const errorCount = [...batchStatuses.values()].filter(b => b.status === 'error').length;

      // All errored
      if (errorCount === totalBatches) {
        reject(new Error('Tat ca batches deu that bai'));
        return;
      }

      // Doc pending_sync
      let syncRows: any[] = [];
      for (let attempt = 0; attempt < 3; attempt++) {
        // US-19-01 Task 3: prompt/raw moved to private job_debug_logs (admin endpoint).
        const { data } = await supabase.from('pending_sync')
          .select('batch_index, result, type, job_id')
          .eq('run_id', runId)
          .order('batch_index', { ascending: true });

        if (data && data.length >= doneCount) {
          syncRows = data;
          break;
        }
        if (attempt < 2) await new Promise(r => setTimeout(r, 500));
      }

      // Song song: DELETE pending_sync + UPDATE jobs.synced_at = now() cho từng job_id để audit.
      const syncedJobIds = syncRows.map(r => r.job_id).filter(Boolean);
      await Promise.allSettled([
        supabase.from('pending_sync').delete().eq('run_id', runId),
        syncedJobIds.length > 0
          ? supabase.from('jobs').update({ synced_at: new Date().toISOString() }).in('id', syncedJobIds)
          : Promise.resolve({ error: null }),
      ]);

      // Aggregate usage
      const usage = { inputTokens: 0, outputTokens: 0, cost: 0 };
      for (const b of batchStatuses.values()) {
        usage.inputTokens += b.inputTokens || 0;
        usage.outputTokens += b.outputTokens || 0;
        usage.cost += b.costUsd || 0;
      }

      resolve({ syncRows, usage, doneCount, errorCount });
    };

    // Handle job update
    const handleJobUpdate = (row: any) => {
      if (settled) return;
      if (row.run_id !== runId) return;

      batchStatuses.set(row.batch_index, {
        status: row.status,
        inputTokens: row.input_tokens || 0,
        outputTokens: row.output_tokens || 0,
        costUsd: row.cost_usd || 0,
        error: row.error,
      });

      if (row.status === 'done' || row.status === 'error') {
        checkComplete();
      }
    };

    // Subscribe Realtime
    const channel = supabase.channel(`batch-wait-${runId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'jobs',
        filter: `run_id=eq.${runId}`,
      }, (payload) => {
        handleJobUpdate(payload.new);
      })
      .subscribe();

    // Polling fallback moi 5s
    const pollInterval = setInterval(async () => {
      if (settled) return;
      const { data } = await supabase.from('jobs')
        .select('batch_index, status, error, input_tokens, output_tokens, cost_usd, run_id')
        .eq('run_id', runId);
      if (data) {
        for (const row of data) {
          handleJobUpdate(row);
        }
      }
    }, 5000);

    // Initial fetch (co the job da done truoc khi subscribe)
    (async () => {
      const { data } = await supabase.from('jobs')
        .select('batch_index, status, error, input_tokens, output_tokens, cost_usd, run_id')
        .eq('run_id', runId);
      if (data) {
        for (const row of data) {
          handleJobUpdate(row);
        }
      }
    })();
  });
}

// ===== Response Types (giu lai cho frontend su dung) =====

export interface QCParsedResult {
  scores: {
    overall: number;
    accuracy: number;
    terminology: number;
    style: number;
    fluency: number;
    completeness: number;
  };
  segments: Array<{
    sourceIndex: number;
    score: number;
    issues: string;
    original: string;
    suggestion: string;
  }>;
  evaluation: Record<string, string>;
  summary: string;
}

export interface QualityCheckApiResponse {
  result: QCParsedResult;
  usage: { inputTokens: number; outputTokens: number; cost: number };
  balance?: number;
  errorId?: string;
}

export interface AuditApiResponse {
  result: {
    overallScore: number;
    componentScores: { accuracy: number; terminology: number; tone: number };
    segmentMappings: Array<{
      sourceIndex: number;
      humanIndices: number[];
      humanIndicesRaw?: string;
      isMissing: boolean;
      isPartialMatch?: boolean;
      score: number;
      rules: string;
      errors: string;
      reviews: string;
      notes: string;
    }>;
    criticalErrors: Array<{
      original: string;
      ai_choice: string;
      better_choice: string;
      reason: string;
      severity: string;
    }>;
    glossaryRules: Array<{
      source: string;
      target: string;
      type: string;
      reason: string;
    }>;
    accuracyAnalysis: string;
    terminologyAnalysis: string;
    toneAnalysis: string;
    fluencyAnalysis: string;
    completenessAnalysis: string;
    suggestedPromptUpdate: string;
    isTruncated: boolean;
  };
  usage: { inputTokens: number; outputTokens: number; cost: number };
}

export interface SemanticMatchApiResponse {
  result: {
    mappings: Array<{
      sourceIndex: number;
      humanIndex: number;
      allHumanIndices: number[];
      confidence: number;
      sourceText: string;
      humanText: string;
      isUnmatched: boolean;
    }>;
    alignedHumanSegments: string[];
    unmatchedCount: number;
  };
  usage: { inputTokens: number; outputTokens: number; cost: number };
}

// ===== Pending Sync =====

export interface PendingSyncBatch {
  id: string;
  file_id: string;
  file_name: string;
  batch_index: number;
  total_batches: number;
  status: 'pending' | 'queued' | 'running' | 'done' | 'error';
  result: { translated: string[] } | null;
  usage_data: { inputTokens: number; outputTokens: number; cost: number } | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export async function getPendingSync(fileId?: string): Promise<PendingSyncBatch[]> {
  try {
    const query = fileId ? `?fileId=${fileId}` : '';
    const data = await callWorkersMethod<{ batches: PendingSyncBatch[] }>(`/api/pending-sync${query}`, 'GET');
    return data.batches || [];
  } catch {
    return [];
  }
}

export async function deletePendingSyncData(fileId: string): Promise<void> {
  try {
    await callWorkersMethod<any>(`/api/pending-sync?fileId=${fileId}`, 'DELETE');
  } catch {
    // Best-effort
  }
}

// ===== Pending Results (KV Cache -- legacy) =====

interface PendingResult {
  type: 'translate' | 'quality_check';
  requestId: string;
  userId: string;
  result: any;
  usage: { inputTokens: number; outputTokens: number; cost: number };
  createdAt: string;
}

export async function loadPendingResults(): Promise<PendingResult[]> {
  try {
    const data = await callWorkersMethod<{ results: PendingResult[] }>('/api/pending', 'GET');
    return data.results || [];
  } catch {
    return [];
  }
}

export async function acknowledgePendingResult(requestId: string): Promise<void> {
  try {
    await callWorkersMethod<any>(`/api/pending?requestId=${requestId}`, 'DELETE');
  } catch {
    // Best-effort
  }
}

// ===== Health Check =====

export async function checkWorkersHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${WORKERS_URL}/api/health`);
    return response.ok;
  } catch {
    return false;
  }
}

// ===== DEPRECATED: SSE Functions (se xoa sau khi migrate het frontend) =====

const WORKERS_URL_INTERNAL = WORKERS_URL;

/** @deprecated -- dang migrate sang insertJob */
async function callWorkersSSE<T>(
  endpoint: string, body: any,
  onChunk?: (text: string) => void, onThinking?: (text: string) => void
): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(`${WORKERS_URL_INTERNAL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error((error as any).error || `Loi server: ${response.status}`);
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n');
    buffer = parts.pop() || '';
    for (const line of parts) {
      const trimmed = line.trim();
      if (trimmed.startsWith('event:')) { currentEvent = trimmed.slice(6).trim(); }
      else if (trimmed.startsWith('data:')) {
        const data = trimmed.slice(5).trim();
        if (currentEvent === 'result' && data) { reader.cancel(); return JSON.parse(data) as T; }
        else if (currentEvent === 'error' && data) { reader.cancel(); throw new Error(JSON.parse(data).error || 'Loi server'); }
        else if (currentEvent === 'chunk' && data && onChunk) { try { onChunk(JSON.parse(data).text || ''); } catch {} }
        else if (currentEvent === 'thinking' && data && onThinking) { try { onThinking(JSON.parse(data).text || ''); } catch {} }
        currentEvent = '';
      } else if (trimmed === '') { currentEvent = ''; }
    }
  }
  throw new Error('SSE stream ended without result');
}

/** @deprecated */
export async function translateViaWorkers(
  segments: string[], config: Record<string, any>, requestId?: string, onChunk?: (text: string) => void
) {
  const { fileId, fileName, batchIndex, totalBatches, ...restConfig } = config;
  return callWorkersSSE<any>('/api/translate', {
    requestId: requestId || generateRequestId(), segments, config: restConfig,
    ...(fileId ? { fileId, fileName, batchIndex, totalBatches } : {}),
  }, onChunk);
}

/** @deprecated */
export async function qualityCheckViaWorkers(
  sourceSegments: string[], aiSegments: string[], config: Record<string, any>,
  matchedRules?: string, requestId?: string
): Promise<QualityCheckApiResponse> {
  return callWorkersSSE<QualityCheckApiResponse>('/api/quality-check', {
    requestId: requestId || generateRequestId(), sourceSegments, aiSegments, config, matchedRules,
  });
}

/** @deprecated */
export async function auditViaWorkers(
  sourceSegments: string[], aiSegments: string[], humanSegments: string[],
  config: Record<string, any>, matchedRules?: string, requestId?: string,
  onChunk?: (text: string) => void, onThinking?: (text: string) => void
): Promise<AuditApiResponse> {
  return callWorkersSSE<AuditApiResponse>('/api/audit', {
    requestId: requestId || generateRequestId(), sourceSegments, aiSegments, humanSegments, config, matchedRules,
  }, onChunk, onThinking);
}

/** @deprecated */
export async function semanticMatchViaWorkers(
  viSegments: string[], enSegments: string[], model?: string, requestId?: string
): Promise<SemanticMatchApiResponse> {
  return callWorkersSSE<SemanticMatchApiResponse>('/api/semantic-match', {
    requestId: requestId || generateRequestId(), viSegments, enSegments, model,
  });
}

/** @deprecated */
export async function submitTranslationJob(request: any): Promise<any> {
  throw new Error('submitTranslationJob removed -- use insertJob instead');
}

/** @deprecated */
export async function processBatchViaWorker(userId: string, fileId: string, batchIndex: number): Promise<void> {
  throw new Error('processBatchViaWorker removed -- use insertJob instead');
}

